import { scoreToColor } from './areaData.js';
import { getAreaFacts } from './tooltipData.js';
import { scoreWord } from './scoreWords.js';
import { DEFAULT_TREND_YEARS, fetchAreaSeries, priceChangeOverYears } from './areaPriceTrend.js';
import { apiFetch } from './edgePayloads.js';

// What a comparison shows for each area, shared by the comparison card in the views (CompareCard.js) and the
// comparison's own page (src/area/ComparePage.js), so the two read the same.

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'https://api.xn--omrdeskollen-vcb.nu';
const NBSP = ' ';

// The measures, and what the best one is called ("Högst" income, "Lägst" unemployment: a high score is the
// better side). Prices get no mark: dearer isn't better.
export const COMPARE_ROWS = [
    { key: 'income', sv: 'Inkomst', en: 'Income', top: { sv: 'Högst', en: 'Highest' } },
    { key: 'owned', sv: 'Äger sitt boende', en: 'Own their home', top: { sv: 'Flest', en: 'Most' } },
    { key: 'education', sv: 'Högutbildade', en: 'Highly educated', top: { sv: 'Flest', en: 'Most' } },
    { key: 'unemployment', sv: 'Arbetslöshet', en: 'Unemployment', top: { sv: 'Lägst', en: 'Lowest' } },
    { key: 'apartmentPrice', sv: 'Lägenhet per m²', en: 'Apartment per m²' },
    { key: 'housePrice', sv: 'Villa per m²', en: 'House per m²' },
];

export const formatScore = (score, swedish) => (swedish ? String(score).replace('.', ',') : String(score));

// One area: its score, colour and word, and its six numbers by row key ({ value, color, score }).
export const compareArea = (code, name, colorCache, areaStats, swedish) => {
    if (!code || !colorCache || !areaStats) return null;
    const raw = Number(areaStats[`combined_${code}`]);
    const score = Number.isFinite(raw) ? raw : null;
    const details = getAreaFacts(code, colorCache, areaStats, swedish);
    const facts = Object.fromEntries([...details.facts, ...details.prices].map((fact) => [fact.key, fact]));
    // The prices' colour dots come from the same metric colours as the facts'.
    facts.apartmentPrice.color = colorCache[`sqm_price_apartment_${code}`] || 'var(--area-card-dot-empty)';
    facts.housePrice.color = colorCache[`sqm_price_house_${code}`] || 'var(--area-card-dot-empty)';
    return {
        code,
        name: name || code,
        score,
        colour: score != null ? (scoreToColor[score] || '#8f9a97') : '#8f9a97',
        word: score != null ? scoreWord(score, swedish) : '',
        facts,
    };
};

const numberOf = (fact) => {
    const match = String(fact?.value || '').replace(/[\s ]/g, '').replace(',', '.').match(/-?\d+(\.\d+)?/);
    return match ? Number(match[0]) : null;
};

// Which areas are best on each marked row, by the number shown (lowest for unemployment); none when level or
// fewer than two have a number. { rowKey: Set(codes) }
export const compareTops = (areas) => {
    const result = {};
    COMPARE_ROWS.filter((row) => row.top).forEach((row) => {
        const values = areas.map((area) => ({ code: area.code, value: numberOf(area.facts[row.key]) }))
            .filter((item) => Number.isFinite(item.value));
        if (values.length < 2) return;
        const pick = row.key === 'unemployment' ? Math.min : Math.max;
        const best = pick(...values.map((item) => item.value));
        if (values.every((item) => item.value === best)) return;
        result[row.key] = new Set(values.filter((item) => item.value === best).map((item) => item.code));
    });
    return result;
};

// The price trend: the apartment curve's change over the default window, else the villas'. Cached per area.
const trendCache = new Map();
export const fetchAreaTrend = (code) => {
    if (trendCache.has(code)) return trendCache.get(code);
    const promise = fetchAreaSeries(code, 'apartment')
        .then((data) => data || fetchAreaSeries(code, 'house'))
        .then((data) => (data ? priceChangeOverYears(data.series, DEFAULT_TREND_YEARS) : null))
        .catch(() => null);
    trendCache.set(code, promise);
    return promise;
};

export const formatTrend = (trend, swedish, { long = false } = {}) => {
    if (!trend) return '';
    const value = `${trend.change >= 0 ? '↗ +' : '↘ −'}${Math.abs(trend.change)}${NBSP}%`;
    if (!long) return value;
    return `${value} ${trend.full
        ? (swedish ? `sedan ${trend.startYear}` : `since ${trend.startYear}`)
        : (swedish ? `på ${DEFAULT_TREND_YEARS} år` : `in ${DEFAULT_TREND_YEARS} years`)}`;
};

// How many homes are for sale in the area (the server's count, the same the page and listvy use). Cached for the
// session; null when it can't be had.
const totalCache = new Map();
export const fetchAreaHomesTotal = (code) => {
    if (totalCache.has(code)) return totalCache.get(code);
    const promise = apiFetch(`${API_BASE}/listings/active/regso/${encodeURIComponent(code)}?limit=1&offset=0`)
        .then((response) => (response.ok ? response.json() : null))
        .then((data) => (Number.isFinite(Number(data?.total)) ? Number(data.total) : null))
        .catch(() => null);
    totalCache.set(code, promise);
    // A failure is tried again next time.
    promise.then((value) => { if (value == null) totalCache.delete(code); });
    return promise;
};
